
import React, { useState, useMemo, useEffect } from 'react';
import {
  UserAddOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  Card, Input, Select, Button, Tag, Typography, Space, Table, Pagination,
  Modal, Form, message, Row, Col,
} from 'antd';
import useUsers from "../../hooks/users/useUsers";
import useOrgHierarchy from "../../hooks/projects/useOrgHierarchy";
import { createUser, updateUser, deleteUser } from "../../api/userApi";
import "./Users.css";

const { Text } = Typography;
const { Option } = Select;

const Users = () => {
  const {
    users,
    totalCount,
    roleSummary,
    loading,
    fetchUsers,
  } = useUsers();

  const { 
    zonesList, 
    circlesList, 
      divisionsList, 
    loading: hierarchyLoading 
  } = useOrgHierarchy();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [filterZone, setFilterZone] = useState('All');
  const [filterCircle, setFilterCircle] = useState('All');
  const [filterDivision, setFilterDivision] = useState('All');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const addZone = Form.useWatch("zone_code", form);
  const addCircle = Form.useWatch("circle_code", form);
  const editZone = Form.useWatch("zone_code", editForm);
  const editCircle = Form.useWatch("circle_code", editForm);
  // zone_code -> zone_name (circles/divisions link by name, not code)
  const addZoneName = zonesList.find((z) => z.code === addZone)?.name;
  const editZoneName = zonesList.find((z) => z.code === editZone)?.name;

  const filteredAddCircles = circlesList.filter((c) => !addZone || c.zone_name === addZoneName);
  const filteredEditCircles = circlesList.filter((c) => !editZone || c.zone_name === editZoneName);

  const addCircleName = circlesList.find((c) => c.circle_code === addCircle)?.circle_name;
  const editCircleName = circlesList.find((c) => c.circle_code === editCircle)?.circle_name;

  const filteredAddDivisions = divisionsList.filter((d) => !addCircle || d.circle_name === addCircleName);
  const filteredEditDivisions = divisionsList.filter((d) => !editCircle || d.circle_name === editCircleName);

  const handleAddZoneChange = () => {
    form.setFieldsValue({ circle_code: null, div_code: null });
  };
  const handleAddCircleChange = () => {
    form.setFieldsValue({ div_code: null });
  };
  const handleEditZoneChange = () => {
    editForm.setFieldsValue({ circle_code: null, div_code: null });
  };
  const handleEditCircleChange = () => {
    editForm.setFieldsValue({ div_code: null });
  };
  // Filter options for table — sourced from the full org hierarchy so zones/circles/
  // divisions with no users assigned yet still show up in the dropdowns.
  const filterOptions = useMemo(() => {
    return {
      zones: zonesList,
      circles: circlesList.map((c) => ({ code: c.circle_code, name: c.circle_name })),
      divisions: divisionsList.map((d) => ({ code: d.div_code, name: d.div_name })),
    };
  }, [zonesList, circlesList, divisionsList]);

  const zoneNameByCode = useMemo(() => {
    const map = {};
    zonesList.forEach((z) => { map[z.code] = z.name; });
    return map;
  }, [zonesList]);

  const circleNameByCode = useMemo(() => {
    const map = {};
    circlesList.forEach((c) => { map[c.circle_code] = c.circle_name; });
    return map;
  }, [circlesList]);

  const divisionNameByCode = useMemo(() => {
    const map = {};
    divisionsList.forEach((d) => { map[d.div_code] = d.div_name; });
    return map;
  }, [divisionsList]);

  useEffect(() => {
    fetchUsers({
      page: currentPage,
      page_size: pageSize,
      search: searchTerm,
      role: filterRole,
      zone_code: filterZone,
      circle_code: filterCircle,
      div_code: filterDivision,
    });
  }, [
    currentPage,
    pageSize,
    searchTerm,
    filterRole,
    filterZone,
    filterCircle,
    filterDivision,
  ]);



  const handleAddUser = async (values) => {
    setCreating(true);
    try {
      await createUser(values);
      message.success("User created successfully!");
      setIsAddModalOpen(false);
      form.resetFields();
      fetchUsers();
    } catch (err) {
      message.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      mobile_number: user.mobile_number,
      role: user.role,
      zone_code: user.zone_code,
      circle_code: user.circle_code,
      div_code: user.div_code,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (values) => {
    setUpdating(true);
    try {
      await updateUser(editingUser.id, values);
      message.success("User updated successfully!");
      setIsEditModalOpen(false);
      editForm.resetFields();
      fetchUsers();
    } catch (err) {
      message.error("Failed to update user");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (uuid_token) => {
    try {
      await deleteUser(uuid_token);
      message.success("User deleted successfully");
      fetchUsers();
    } catch (err) {
      message.error("Failed to delete user");
    }
  };

  const columns = [
    { 
      title: 'S.No', 
      key: 'serial', 
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1, 
      width: 70 
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="user-avatar">
            <div className="avatar-initials">
              {(record.first_name?.[0] || '') + (record.last_name?.[0] || '')}
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{record.first_name} {record.last_name}</div>
            <div style={{ color: '#666', fontSize: '13px' }}>{record.email}</div>
          </div>
        </div>
      ),
    },
    { 
      title: 'Role', 
      dataIndex: 'role', 
      render: (role) => <Tag color="#da241c" style={{ borderRadius: '20px' }}>{role?.replace(/_/g, ' ')}</Tag> 
    },
    {
      title: 'Location',
      key: 'location',
      render: (_, r) => {
        const zoneName = r.zone_name || zoneNameByCode[r.zone_code] || r.zone_code || '-';
        const circleName = r.circle_name || circleNameByCode[r.circle_code] || r.circle_code || '-';
        return (
          <div>
            <div>{zoneName}</div>
            <small style={{ color: '#888' }}>{circleName}</small>
          </div>
        );
      },
    },
    {
      title: 'Division',
      key: 'division',
      render: (_, r) => r.div_name || divisionNameByCode[r.div_code] || '-',
    },
    { 
      title: 'Status', 
      key: 'status', 
      render: () => <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag> 
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            style={{ color: '#da241c' }} 
            onClick={() => openEditModal(record)} 
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => {
              Modal.confirm({
                title: "Delete User",
                content: "Are you sure you want to permanently delete this user?",
                okText: "Delete",
                okButtonProps: { danger: true },
                onOk: () => handleDeleteUser(record.id),
              });
            }} 
          />
        </Space>
      ),
    },
  ];

  const roleCards = [
    {
      title: "Superintending Engineer",
      key: "SUPERINTENDING_ENGINEER",
      className: "active",
      icon: <CheckCircleOutlined style={{ fontSize: 28 }} />,
    },
    {
      title: "Executive Engineer",
      key: "EXECUTIVE_ENGINEER",
      className: "inactive",
      icon: <CheckCircleOutlined style={{ fontSize: 28 }} />,
    },
    {
      title: "Additional Executive Engineer",
      key: "ADDITIONAL_EXECUTIVE_ENGINEER",
      className: "new",
      icon: <UserAddOutlined style={{ fontSize: 28 }} />,
    },
  ];

  const onSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const onRoleChange = (value) => {
    setFilterRole(value);
    setCurrentPage(1);
  };

  const onZoneChange = (value) => {
    setFilterZone(value);
    setCurrentPage(1);
  };

  const onCircleChange = (value) => {
    setFilterCircle(value);
    setCurrentPage(1);
  };

  const onDivisionChange = (value) => {
    setFilterDivision(value);
    setCurrentPage(1);
  };

  const onPageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  };

  return (
    <div className="users-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <Card className="stat-card total">
          <div className="stat-icon">
            <TeamOutlined style={{ fontSize: 28 }} />
          </div>
          <div>
            <h5>Total Users</h5>
            <p className="stat-value">{totalCount}</p>
          </div>
        </Card>

        {roleCards.map((role) => (
          <Card key={role.key} className={`stat-card ${role.className}`}>
            <div className="stat-icon">{role.icon}</div>
            <div>
              <h5>{role.title}</h5>
              <p className="stat-value">{roleSummary?.[role.key] || 0}</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <div className="user-toolbar">
        <div className="toolbar-row">
          <div className="toolbar-search">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={onSearchChange}
              allowClear
              size="large"
            />
          </div>

          <div className="toolbar-filters">
            <Select value={filterRole} onChange={onRoleChange} size="large" className="toolbar-select">
              <Option value="All">All Roles</Option>
              <Option value="CHIEF_ENGINEER">Chief Engineer</Option>
              <Option value="SUPERINTENDING_ENGINEER">Superintending Engineer</Option>
              <Option value="EXECUTIVE_ENGINEER">Executive Engineer</Option>
              <Option value="ADDITIONAL_EXECUTIVE_ENGINEER">Additional Executive Engineer</Option>
            </Select>

            <Select value={filterZone} onChange={onZoneChange} size="large" className="toolbar-select">
              <Option value="All">All Zones</Option>
              {filterOptions.zones.map((zone) => (
                <Option key={zone.code} value={zone.code}>
                  {zone.code} - {zone.name}
                </Option>
              ))}
            </Select>

            <Select value={filterCircle} onChange={onCircleChange} size="large" className="toolbar-select">
              <Option value="All">All Circles</Option>
              {filterOptions.circles.map((circle) => (
                <Option key={circle.code} value={circle.code}>
                  {circle.code} - {circle.name}
                </Option>
              ))}
            </Select>

            <Select value={filterDivision} onChange={onDivisionChange} size="large" className="toolbar-select">
              <Option value="All">All Divisions</Option>
              {filterOptions.divisions.map((division) => (
                <Option key={division.code} value={division.code}>
                  {division.code} - {division.name}
                </Option>
              ))}
            </Select>

            <Button className="clear-user-btn" onClick={() => {
              setSearchTerm("");
              setFilterRole("All");
              setFilterZone("All");
              setFilterCircle("All");
              setFilterDivision("All");
              setCurrentPage(1);
            }}>
              Clear
            </Button>

            <Button type="primary" icon={<UserAddOutlined />} className="add-user-btn" onClick={() => setIsAddModalOpen(true)}>
              Add User
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <Card bodyStyle={{ padding: 0 }} className="users-table-card">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="uuid_token"
          pagination={false}
          loading={loading}
          scroll={{ x: 900 }}
        />
        <div className="pagination-section">
          <Text type="secondary">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
          </Text>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={onPageChange}
          />
        </div>
      </Card>

      {/* Add User Modal */}
      <Modal
        title="Create New User"
        open={isAddModalOpen}
        closable={false}
        onCancel={() => { setIsAddModalOpen(false); form.resetFields(); }}
        footer={null}
        width={600}
        centered
        className="slick-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleAddUser}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12}><Form.Item label="First Name" name="first_name" rules={[{ required: true }]}><Input size="large" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Last Name" name="last_name" rules={[{ required: true }]}><Input size="large" /></Form.Item></Col>

            <Col xs={24} sm={12}><Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}><Input size="large" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Mobile Number" name="mobile_number"><Input maxLength={10} size="large" /></Form.Item></Col>

            <Col xs={24} sm={12}><Form.Item label="Password" name="password" rules={[{ required: true }]}><Input.Password size="large" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Role" name="role" rules={[{ required: true }]}><Select size="large">
              <Option value="CHIEF_ENGINEER">Chief Engineer</Option>
              <Option value="SUPERINTENDING_ENGINEER">Superintending Engineer</Option>
              <Option value="EXECUTIVE_ENGINEER">Executive Engineer</Option>
              <Option value="ADDITIONAL_EXECUTIVE_ENGINEER">Additional Executive Engineer</Option>
            </Select></Form.Item></Col>

            <Col xs={24} sm={8}>
              <Form.Item label="Zone" name="zone_code">
                <Select placeholder="Select Zone" size="large" allowClear loading={hierarchyLoading} onChange={handleAddZoneChange}>
                  {zonesList.map((z) => (
                    <Option key={z.code} value={z.code}>
                      {z.code} - {z.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

          <Col xs={24} sm={8}>
            <Form.Item label="Circle" name="circle_code">
              <Select placeholder="Select Circle" size="large" allowClear loading={hierarchyLoading} onChange={handleAddCircleChange}>
                {filteredAddCircles.map((c) => (
                  <Option key={c.circle_code} value={c.circle_code}>
                    {c.circle_code} - {c.circle_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={8}>
            <Form.Item label="Division" name="div_code">
              <Select placeholder="Select Division" size="large" allowClear loading={hierarchyLoading}>
                {filteredAddDivisions.map((d) => (
                  <Option key={d.div_code} value={d.div_code}>
                    {d.div_code} - {d.div_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          </Row>

          <div className="modal-footer">
            <Button onClick={() => { setIsAddModalOpen(false); form.resetFields(); }}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={creating} style={{ background: '#da241c', borderColor: '#da241c' }}>
              Create User
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit User Modal */}
     {/* Edit User Modal */}
    <Modal
      title="Edit User Profile"
      open={isEditModalOpen}
      closable={false}
      onCancel={() => { 
        setIsEditModalOpen(false); 
        editForm.resetFields(); 
      }}
      footer={null}
      width={600}
      centered
      className="slick-modal"
    >
      <Form form={editForm} layout="vertical" onFinish={handleUpdateUser}>
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12}>
            <Form.Item label="First Name" name="first_name" rules={[{ required: true }]}>
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Last Name" name="last_name" rules={[{ required: true }]}>
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Mobile Number" name="mobile_number">
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Role" name="role" rules={[{ required: true }]}>
              <Select size="large">
                <Option value="CHIEF_ENGINEER">Chief Engineer</Option>
                <Option value="SUPERINTENDING_ENGINEER">Superintending Engineer</Option>
                <Option value="EXECUTIVE_ENGINEER">Executive Engineer</Option>
                <Option value="ADDITIONAL_EXECUTIVE_ENGINEER">Additional Executive Engineer</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Zone" name="zone_code">
              <Select 
                placeholder="Select Zone" 
                size="large" 
                allowClear 
                loading={hierarchyLoading} 
                onChange={handleEditZoneChange}
              >
                {zonesList.map((z) => (
                  <Option key={z.code} value={z.code}>
                    {z.code} - {z.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Circle" name="circle_code">
              <Select 
                placeholder="Select Circle" 
                size="large" 
                allowClear 
                loading={hierarchyLoading}
                onChange={handleEditCircleChange}
              >
                {filteredEditCircles.map((c) => (
                  <Option key={c.circle_code} value={c.circle_code}>
                    {c.circle_code} - {c.circle_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Division" name="div_code">
              <Select 
                placeholder="Select Division" 
                size="large" 
                allowClear 
                loading={hierarchyLoading}
              >
                {filteredEditDivisions.map((d) => (   
                  <Option key={d.div_code} value={d.div_code}>
                    {d.div_code} - {d.div_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="modal-footer">
          <Button 
            onClick={() => { 
              setIsEditModalOpen(false); 
              editForm.resetFields(); 
            }}
          >
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={updating} 
            style={{ background: '#da241c', borderColor: '#da241c' }}
          >
            Update User
          </Button>
        </div>
      </Form>
    </Modal>
    </div>
  );
};

export default Users;